import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { ThreeCanvas } from "@remotion/three";
import { KuroMesh, SparkMesh } from "./KuroAndSpark3D";

export const KuroAndSparkShowcase: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Subtle studio camera dolly & breath
  const camBob = Math.sin(frame * 0.04) * 0.04;

  return (
    <div
      style={{
        width,
        height,
        backgroundColor: "#070B0E",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      {/* --- BACKGROUND ATMOSPHERE (FOREST SLATE & NEON GLOW) --- */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(circle at 30% 45%, rgba(0, 229, 255, 0.09) 0%, transparent 48%),
            radial-gradient(circle at 75% 35%, rgba(255, 143, 0, 0.12) 0%, transparent 45%),
            linear-gradient(to bottom, rgba(10, 20, 26, 0.7), rgba(7, 11, 14, 0.98))
          `,
        }}
      />

      {/* --- 3D THREE.JS CANVAS STAGE --- */}
      <div style={{ position: "absolute", inset: 0, zIndex: 10 }}>
        <ThreeCanvas
          width={width}
          height={height}
          camera={{
            fov: 36,
            position: [0, 0.35 + camBob, 4.6],
          }}
          shadows
        >
          {/* Ambient Foundation */}
          <ambientLight intensity={0.9} color="#D1E8E2" />

          {/* Primary Warm Key Light (Top-Right) */}
          <directionalLight
            castShadow
            position={[4, 7, 5]}
            intensity={3.4}
            color="#FFF4E6"
            shadow-mapSize={[2048, 2048]}
            shadow-bias={-0.0003}
          />

          {/* Secondary Emerald Fill Light (Front-Left) */}
          <directionalLight position={[-4, 3, 3]} intensity={1.5} color="#A7F3D0" />

          {/* Rim Light 1: Laser Cyan on Kuro (Left/Back) */}
          <directionalLight position={[-5, 5, -5]} intensity={4.5} color="#00E5FF" />

          {/* Rim Light 2: Amber Glow on Spark (Right/Back) */}
          <directionalLight position={[5, 6, -5]} intensity={4.0} color="#FF9100" />

          {/* Ground Upward Bounce */}
          <pointLight position={[0, -0.6, 2]} intensity={1.2} color="#80DEEA" distance={5} />

          {/* --- PEBBLE-SANCTUARY STAGE / PLATFORM --- */}
          <group position={[0, -1.05, 0]}>
            {/* Dark Stone Slab */}
            <mesh receiveShadow position={[0, 0, 0]}>
              <cylinderGeometry args={[3.2, 3.2, 0.14, 48]} />
              <meshStandardMaterial color="#161F26" roughness={0.7} metalness={0.1} />
            </mesh>
            <mesh position={[0, -0.12, 0]}>
              <cylinderGeometry args={[3.25, 3.4, 0.16, 48]} />
              <meshStandardMaterial color="#0D1318" roughness={0.8} metalness={0.2} />
            </mesh>
            {/* Glowing Moss Glyph under Kuro */}
            <mesh position={[-0.95, 0.075, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <ringGeometry args={[0.85, 0.92, 36]} />
              <meshBasicMaterial color="#00E5FF" />
            </mesh>
            {/* Glowing Amber Ring under Spark */}
            <mesh position={[0.95, 0.075, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <ringGeometry args={[0.85, 0.92, 36]} />
              <meshBasicMaterial color="#FF9100" />
            </mesh>
          </group>

          {/* ── CHARACTER 1: KURO (PEBBLE GOLEM - GROUNDED) ── */}
          <group position={[-0.95, -0.98, 0]} rotation={[0, 0.28, 0]}>
            <KuroMesh
              frame={frame}
              fps={fps}
              isSpeaking={frame % 130 < 65}
              isExcited={frame % 180 > 140}
            />
          </group>

          {/* ── CHARACTER 2: SPARK (VACUUM-TUBE JELLY - HOVERING) ── */}
          <group position={[0.95, -0.5, 0]} rotation={[0, -0.28, 0]}>
            <SparkMesh
              frame={frame}
              fps={fps}
              isSpeaking={frame % 130 >= 65}
              isSurprised={frame % 180 > 140}
            />
          </group>
        </ThreeCanvas>
      </div>

      {/* --- HUD HEADER (TIKTOK SAFE ZONE: Y 220px - 1520px) --- */}
      <div
        style={{
          position: "absolute",
          top: 230,
          left: 0,
          right: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 50,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            backgroundColor: "rgba(0, 229, 255, 0.12)",
            border: "1px solid rgba(0, 229, 255, 0.35)",
            padding: "6px 16px",
            borderRadius: 999,
            color: "#00E5FF",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            marginBottom: 10,
            boxShadow: "0 0 20px rgba(0, 229, 255, 0.2)",
          }}
        >
          <span>🌿</span> ARENA DUO • NATURE-TECH FUSION
        </div>
        <h1
          style={{
            color: "#FFFFFF",
            fontSize: 46,
            fontWeight: 800,
            letterSpacing: -1,
            margin: 0,
            textAlign: "center",
            textShadow: "0 4px 20px rgba(0, 0, 0, 0.8)",
          }}
        >
          KURO & SPARK 3D
        </h1>
        <p
          style={{
            color: "#94A3B8",
            fontSize: 17,
            margin: "8px 0 0 0",
            textAlign: "center",
          }}
        >
          Pebble Golem & Vacuum-Tube Jelly • Ultra-Lightweight Three.js
        </p>
      </div>

      {/* --- FOOTER CARD (TIKTOK SAFE ZONE: Y ~1400px) --- */}
      <div
        style={{
          position: "absolute",
          bottom: 380,
          left: 70,
          right: 70,
          backgroundColor: "rgba(13, 20, 26, 0.85)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          backdropFilter: "blur(16px)",
          borderRadius: 24,
          padding: "18px 22px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
          boxShadow: "0 12px 36px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 14,
              backgroundColor: "rgba(0, 229, 255, 0.15)",
              border: "1px solid rgba(0, 229, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
            }}
          >
            🪨
          </div>
          <div>
            <div style={{ color: "#FFFFFF", fontWeight: 700, fontSize: 17 }}>Kuro (Stone Golem)</div>
            <div style={{ color: "#64748B", fontSize: 12 }}>Slate River Pebble • Living Sprout • Optic Visor</div>
          </div>
        </div>

        <div style={{ width: 1, height: 38, backgroundColor: "rgba(255, 255, 255, 0.1)" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 14,
              backgroundColor: "rgba(255, 143, 0, 0.15)",
              border: "1px solid rgba(255, 143, 0, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
            }}
          >
            💡
          </div>
          <div>
            <div style={{ color: "#FFFFFF", fontWeight: 700, fontSize: 17 }}>Spark (Vacuum Droid)</div>
            <div style={{ color: "#64748B", fontSize: 12 }}>Glass Dome • Amber Filament • Ribbon Tendrils</div>
          </div>
        </div>
      </div>
    </div>
  );
};
